// "HOW ROUTEZERO WORKS" — two-row photo marquee of the RouteZero Network (the seven city
// sites and a mission scene from every domain), followed by the three steps of a ticket.
// Presentation only: no timers, no state, nothing stored.
//
// Each row is a flex track (width: max-content) holding the same tiles twice back to back,
// so translateX(0 → -50%) loops with no jump. Row 1: 22s; row 2: 26s, reversed. Only
// transform animates (see 24-photo-marquee.css). Hover or keyboard focus pauses both rows.
// The second set is aria-hidden with empty alt text, so screen readers read each image once.
// Each row is a focusable region (visible focus ring); prefers-reduced-motion: no animation,
// the duplicate set is hidden and the row scrolls natively (mouse, touch, arrow keys).
// Images: original project illustrations only (src/map/imageData.js).
import { h } from './h.js';
import { icon } from './icons.js';
import { curriculum } from '../core/curriculum.js';
import { resolveCityImage, resolveMissionImage } from '../map/imageData.js';

export const HOW_STEPS = [
  { n: '01', cap: 'Read the Ticket', icon: 'file', text: 'Understand what the user is experiencing, what is affected, and how urgent the issue is.' },
  { n: '02', cap: 'Collect Evidence', icon: 'search', text: 'Investigate before you change anything. Compare observed behavior with the expected result.' },
  { n: '03', cap: 'Resolve or Escalate', icon: 'shield', text: 'Finish with verification and good documentation. Apply an appropriate fix, or escalate the issue to the correct team.' },
];
const KIND_LABEL = { LEARN: 'Learn', LAB: 'Lab', TROUBLESHOOTING: 'Troubleshooting', CHALLENGE: 'Final challenge' };
const KIND_SHORT = { LEARN: 'Learn', LAB: 'Lab', TROUBLESHOOTING: 'Triage', CHALLENGE: 'Final' }; // visible chip; full words in the alt text
const CITY = { d01: 'Manila', d02: 'Cebu', d03: 'Davao', d04: 'Batangas', d05: 'Clark', d06: 'Taguig', d07: 'Iloilo' };

/** The tiles: a wide city scene, then a square mission scene, per domain. */
export function marqueeTiles() {
  const kinds = ['LEARN', 'LAB', 'TROUBLESHOOTING', 'CHALLENGE'];
  return curriculum.domains.filter((d) => CITY[d.id]).flatMap((d, i) => {
    const city = resolveCityImage(d.id);
    const kind = kinds[i % kinds.length];
    const mission = resolveMissionImage(d.id, { id: '-' }, kind);
    return [
      !city.fallback && { src: city.src, wide: true, label: `${CITY[d.id]} · Domain ${d.number}`, alt: `${CITY[d.id]} site, Domain ${d.number} (${d.title}): ${city.alt.replace(/^Stylised illustration of /, 'stylised illustration of ')}` },
      !mission.fallback && { src: mission.src, wide: false, label: `${KIND_SHORT[kind]} · D${d.number}`, alt: `Domain ${d.number} ${KIND_LABEL[kind].toLowerCase()} mission: ${mission.alt.replace(/^Stylised illustration: /, 'stylised illustration of ')}` },
    ].filter(Boolean);
  });
}

function tile(t, copy) {
  return h('figure', { class: `rz-mq-tile ${t.wide ? 'is-wide' : ''}` },
    h('img', { src: t.src, alt: copy ? '' : t.alt, width: t.wide ? 640 : 360, height: 360, decoding: 'async', fetchpriority: 'low', draggable: 'false' }),
    h('figcaption', {}, t.label));
}

function row(tiles, i, label) {
  const set = (copy) => h('div', { class: 'rz-mq-set', ...(copy ? { 'aria-hidden': 'true', 'data-copy': '' } : {}) }, tiles.map((t) => tile(t, copy)));
  return h('div', { class: `rz-mq-row ${i ? 'is-rev' : ''}`, tabindex: '0', role: 'group', 'aria-label': label, 'data-testid': `marquee-row-${i + 1}` },
    h('div', { class: 'rz-mq-track' }, set(false), set(true)));
}

export function photoMarquee() {
  const tiles = marqueeTiles();
  const half = Math.ceil(tiles.length / 2);
  return h('div', { class: 'rz-mq', 'data-testid': 'photo-marquee' },
    row(tiles.slice(0, half), 0, 'RouteZero Network gallery, row 1'),
    row(tiles.slice(half), 1, 'RouteZero Network gallery, row 2'));
}

export function howItWorks() {
  return h('section', { class: 'rz-section rz-how', id: 'rz-how', 'aria-labelledby': 'rz-how-title', 'data-testid': 'landing-steps' },
    h('div', { class: 'rz-sec-head' },
      h('div', {}, h('span', { class: 'rz-eyebrow' }, 'THE LEARNING JOURNEY'),
        h('h2', { id: 'rz-how-title' }, 'How ', h('span', { class: 'rz-grad' }, 'RouteZero'), ' Works'),
        h('p', {}, 'From the first report to a verified resolution, across seven sites of the RouteZero Network.'))),
    photoMarquee(),
    h('ol', { class: 'rz-tf-captions rz-how-steps' }, HOW_STEPS.map((s, i) => h('li', { class: 'rz-tf-caption', 'data-step': String(i + 1), 'data-testid': `flow-step-${i + 1}` },
      h('span', { class: `rz-orb sm o${i + 1}` }, icon(s.icon, { size: 18 })),
      h('div', { class: 'rz-tf-cap-body' },
        h('h3', {}, h('span', { class: 'rz-how-n' }, `Step ${s.n}`), s.cap),
        h('p', {}, s.text))))));
}
